import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCategories,
  fetchFacts,
  adminLogin,
  createCategory,
  updateCategory,
  deleteCategory,
  createFact,
  updateFact,
 deleteFact,
bulkImportFacts,
exportBackup,
importBackup,
getErrorReports,
getLogicReports,
getFactSuggestions,
deleteErrorReport,
deleteLogicReport,
approveFactSuggestion,
rejectFactSuggestion,
resetStatistics,
  ADMIN_KEY_STORAGE,
} from "@/lib/api";
import { CATEGORY_GROUPS, CATEGORY_GROUP_BY_ID, getCategoryGroup, inferCategoryGroup } from "@/lib/categoryGroups";
import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster, toast } from "sonner";
import { Lock, Plus, Trash2, Pencil, LogOut, FolderOpen, AlertTriangle, CheckCircle2, Eye, EyeOff, Users, Timer } from "lucide-react";

function LockScreen({ onUnlock }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(code);
      localStorage.setItem(ADMIN_KEY_STORAGE, code);
      onUnlock();
      toast.success("Yönetim paneline hoş geldiniz.");
    } catch (err) {
      toast.error("Yanlış şifre.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper bg-grain">
      <Header />
      <main className="relative z-10 max-w-md mx-auto mt-24 px-4">
        <div className="rounded-2xl border border-[#e8eae3] bg-white p-8 fact-card-shadow">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-full bg-[#2e4733] flex items-center justify-center">
              <Lock size={16} className="text-[#f5f6f2]" />
            </div>
            <div>
              <h1 className="font-serif-fact text-2xl text-[#1c2723]">Yönetim</h1>
              <p className="label-eyebrow text-[#a3aba6]">Erişim şifresi gerekli</p>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <Input
              data-testid="admin-passcode-input"
              type="password"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Yönetici şifresi"
              autoFocus
              required
            />
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full h-11 rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
            >
              {loading ? "Doğrulanıyor..." : "Gir"}
            </Button>
          </form>
          
        </div>
      </main>
      <Toaster />
    </div>
  );
}

function CategoryDialog({ open, onOpenChange, initial, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [groupId, setGroupId] = useState(initial ? getCategoryGroup(initial) : "egitim");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Kategori adı zorunludur.");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await updateCategory(initial.id, { name, description, group_id: groupId });
        toast.success("Kategori güncellendi.");
      } else {
        await createCategory({ name, description, group_id: groupId || inferCategoryGroup(name) });
        toast.success("Kategori eklendi.");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="category-dialog" className="rounded-xl">
        <DialogHeader>
          <DialogTitle className="font-serif-fact text-xl">
            {initial?.id ? "Kategoriyi düzenle" : "Yeni kategori"}
          </DialogTitle>
          <DialogDescription>
            Konu başlığı ve kısa bir açıklama girin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="category-name-input"
            placeholder="Örn: Anayasa Hukuku · Temel Haklar"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Textarea
            data-testid="category-desc-input"
            placeholder="Kısa açıklama (opsiyonel)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <Select value={groupId} onValueChange={setGroupId}>
            <SelectTrigger data-testid="category-group-select" className="rounded-xl">
              <SelectValue placeholder="Ders grubu seçin" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_GROUPS.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            data-testid="category-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FactDialog({ open, onOpenChange, initial, categoryId, onSaved }) {
  const [text, setText] = useState(initial?.text || "");
  const [isTrue, setIsTrue] = useState(initial?.is_true !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) {
      toast.error("Bilgi metni boş olamaz.");
      return;
    }
    setSaving(true);
    try {
      if (initial?.id) {
        await updateFact(initial.id, { text, is_true: isTrue });
        toast.success("Bilgi güncellendi.");
      } else {
        await createFact({ category_id: categoryId, text, is_true: isTrue });
        toast.success("Bilgi eklendi.");
      }
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fact-dialog" className="rounded-xl">
        <DialogHeader>
          <DialogTitle className="font-serif-fact text-xl">
            {initial?.id ? "Bilgiyi düzenle" : "Yeni bilgi"}
          </DialogTitle>
          <DialogDescription>
            Her bilgi tek bir cümle olmalı — öğrencinin okumasını kolaylaştırır.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          data-testid="fact-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder="Örn: Hak ehliyeti haklara sahip olabilme yeteneğidir, fiil ehliyeti hakları bizzat kullanabilme yeteneğidir."
        />
        <div className="flex items-center gap-2 mt-1">
          <span className="label-eyebrow mr-2">Türü:</span>
          <button
            type="button"
            data-testid="fact-mark-true"
            onClick={() => setIsTrue(true)}
            className={`h-9 px-4 rounded-full text-sm transition-colors ${
              isTrue
                ? "bg-[#2e4733] text-[#f5f6f2]"
                : "bg-transparent border border-[#e8eae3] text-[#5c635e] hover:bg-[#f5f6f2]"
            }`}
          >
            Doğru bilgi
          </button>
          <button
            type="button"
            data-testid="fact-mark-false"
            onClick={() => setIsTrue(false)}
            className={`h-9 px-4 rounded-full text-sm transition-colors ${
              !isTrue
                ? "bg-[#a55e3f] text-white"
                : "bg-transparent border border-[#e8eae3] text-[#5c635e] hover:bg-[#f5f6f2]"
            }`}
          >
            Yanlış cümle
          </button>
        </div>
        <DialogFooter>
          <Button
            data-testid="fact-save-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const [unlocked, setUnlocked] = useState(
    !!localStorage.getItem(ADMIN_KEY_STORAGE)
  );
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [facts, setFacts] = useState([]);
  const [loadingFacts, setLoadingFacts] = useState(false);

  const [catDialog, setCatDialog] = useState({ open: false, initial: null });
  const [factDialog, setFactDialog] = useState({ open: false, initial: null });
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [logicReports, setLogicReports] = useState([]);
  const [suggestions, setSuggestions] = useState([]);

  const loadCategories = async () => {
    try {
      const data = await fetchCategories({ includeHidden: true });
      setCategories(data);
      if (!selectedCategoryId && data.length > 0) {
        setSelectedCategoryId(data[0].id);
      }
    } catch {
      toast.error("Kategoriler yüklenemedi.");
    }
  };

  const loadFacts = async (catId) => {
    if (!catId) return;
    setLoadingFacts(true);
    try {
      const data = await fetchFacts(catId);
      setFacts(data);
    } catch {
      toast.error("Bilgiler yüklenemedi.");
    } finally {
      setLoadingFacts(false);
    }
  };

  const loadReports = async () => {
    try {
      const data = await getErrorReports();
      setReports(data);
    } catch {
      toast.error("Hata bildirimleri yüklenemedi.");
    }
  };

  const loadLogicReports = async () => {
    try {
      const data = await getLogicReports();
      setLogicReports(data);
    } catch {
      toast.error("Mantık bildirimleri yüklenemedi.");
    }
  };

  const loadSuggestions = async () => {
    try {
      const data = await getFactSuggestions();
      setSuggestions(data);
    } catch {
      toast.error("Öneriler yüklenemedi.");
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (unlocked) {
      loadCategories();
      loadReports();
      loadLogicReports();
      loadSuggestions();
    }
  }, [unlocked]);

  useEffect(() => {
    if (selectedCategoryId) loadFacts(selectedCategoryId);
  }, [selectedCategoryId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (!unlocked) {
    return <LockScreen onUnlock={() => setUnlocked(true)} />;
  }

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
  const groupedCategories = CATEGORY_GROUPS.map((group) => ({
    ...group,
    categories: categories.filter((category) => getCategoryGroup(category) === group.id),
  }));

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_KEY_STORAGE);
    setUnlocked(false);
  };

  const handleDeleteCategory = async (cat) => {
    if (!window.confirm(`"${cat.name}" kategorisini ve içindeki tüm bilgileri silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteCategory(cat.id);
      toast.success("Kategori silindi.");
      if (selectedCategoryId === cat.id) {
        setSelectedCategoryId(null);
        setFacts([]);
      }
      loadCategories();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız.");
    }
  };

  const handleToggleCategoryVisibility = async (cat) => {
    await updateCategory(cat.id, { hidden: !cat.hidden });
    toast.success(cat.hidden ? "Kategori görünür yapıldı." : "Kategori gizlendi.");
    await loadCategories();
  };

  const handleCategoryGroupChange = async (cat, groupId) => {
    await updateCategory(cat.id, { group_id: groupId });
    toast.success(`${cat.name}, ${CATEGORY_GROUP_BY_ID[groupId]?.name || "ders grubuna"} taşındı.`);
    await loadCategories();
  };

  const handleDeleteFact = async (f) => {
    if (!window.confirm("Bu bilgiyi silmek istediğinize emin misiniz?")) return;
    try {
      await deleteFact(f.id);
      toast.success("Bilgi silindi.");
      loadFacts(selectedCategoryId);
      loadCategories(); // counts may change
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silme başarısız.");
    }
  };

  const handleOpenReportCategory = (report) => {
    setSelectedCategoryId(report.category_id);
    toast.info("Bildirimdeki kategori açıldı. Kart metnini listedeki bilgilerden kontrol edebilirsiniz.");
  };

  const handleDeleteReport = async (report) => {
    await deleteErrorReport(report.id);
    await loadReports();
    toast.success("Bildirim listeden kaldırıldı.");
  };

  const handleDeleteLogicReport = async (report) => {
    await deleteLogicReport(report.id);
    await loadLogicReports();
    toast.success("Mantık bildirimi listeden kaldırıldı.");
  };

  const handleApproveSuggestion = async (suggestion) => {
    await approveFactSuggestion(suggestion.id);
    await loadSuggestions();
    await loadCategories();
    if (selectedCategoryId === suggestion.category_id) {
      await loadFacts(selectedCategoryId);
    }
    toast.success("Öneri kart havuzuna eklendi.");
  };

  const handleRejectSuggestion = async (suggestion) => {
    await rejectFactSuggestion(suggestion.id);
    await loadSuggestions();
    toast.success("Öneri reddedildi.");
  };

  const handleResetStatistics = async () => {
    if (
      !window.confirm(
        "Tüm kullanıcı çözüm istatistikleri ve test geçmişi silinecek. Kategoriler ve kartlar korunacak. Emin misiniz?"
      )
    ) {
      return;
    }

    const result = await resetStatistics();
    toast.success(
      `${result.deleted_answers || 0} cevap kaydı ve ${result.deleted_test_sessions || 0} test geçmişi kaydı silindi.`
    );
  };

  const handleBulkImport = async () => {
    if (!bulkText.trim()) {
      toast.error("Yüklenecek JSON boş.");
      return;
    }

    setBulkLoading(true);

    try {
      const items = JSON.parse(bulkText);

      if (!Array.isArray(items)) {
        toast.error("JSON bir dizi olmalı.");
        return;
      }

      const result = await bulkImportFacts(items);

      toast.success(
        `${result.added} kayıt eklendi, ${result.skipped} mükerrer/geçersiz kayıt atlandı.`
      );

      setBulkText("");
      await loadCategories();

      if (selectedCategoryId) {
        await loadFacts(selectedCategoryId);
      }
    } catch (e) {
      toast.error("JSON okunamadı. Formatı kontrol et.");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleExportBackup = async () => {
    const backup = await exportBackup();

    const blob = new Blob([JSON.stringify(backup, null, 2)], {
      type: "application/json",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `liarthur-backup-${date}.json`;
    a.click();

    URL.revokeObjectURL(url);
    toast.success("Yedek dosyası indirildi.");
  };

  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (
      !window.confirm(
        "Mevcut tüm veriler silinip yedekten geri yüklenecek. Emin misiniz?"
      )
    ) {
      e.target.value = "";
      return;
    }

    try {
      const text = await file.text();
      const backup = JSON.parse(text);

      const result = await importBackup(backup);

      toast.success(
        `${result.categories} kategori ve ${result.facts} kart geri yüklendi.`
      );

      await loadCategories();
      setSelectedCategoryId(null);
      setFacts([]);
    } catch {
      toast.error("Yedek dosyası okunamadı veya formatı hatalı.");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-paper bg-grain">
      <Header
        right={
          <button
            data-testid="admin-logout"
            onClick={handleLogout}
            className="flex items-center gap-2 h-9 px-3 rounded-full text-[#5c635e] hover:bg-[#e8eae3] transition-colors text-sm"
          >
            <LogOut size={14} /> Çıkış
          </button>
        }
      />
<section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
  <div className="flex items-center justify-between gap-3 flex-wrap">
    <div>
      <h2 className="font-serif-fact text-xl text-[#1c2723]">
        Liarthur Yedekleme
      </h2>
      <p className="text-sm text-[#5c635e] mt-1">
        Tüm kategori ve kartları JSON dosyası olarak yedekleyebilir veya geri yükleyebilirsiniz.
      </p>
    </div>

    <div className="flex items-center gap-2">
      <Button
        onClick={handleExportBackup}
        className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
      >
        Yedek Al
      </Button>

      <label className="inline-flex items-center justify-center h-10 px-4 rounded-full bg-[#f5f6f2] border border-[#e8eae3] text-sm cursor-pointer hover:bg-[#e8eae3]">
        Yedekten Geri Yükle
        <input
          type="file"
          accept="application/json"
          onChange={handleImportBackup}
          className="hidden"
        />
      </label>
    </div>
  </div>
</section>
      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 pt-10 pb-20">
        <section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="font-serif-fact text-xl text-[#1c2723] flex items-center gap-2">
                <Users size={18} className="text-[#2e4733]" />
                Kullanıcılar ve Çözüm İstatistikleri
              </h2>
              <p className="text-sm text-[#5c635e] mt-1">
                Kayıtlı kullanıcıların çözüm istatistikleri ayrı sayfada görüntülenir.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/admin/users"
                className="inline-flex items-center justify-center h-10 px-4 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors text-sm font-medium"
              >
                İstatistikleri aç
              </Link>
              <Link
                to="/admin/test-history"
                className="inline-flex items-center gap-2 justify-center h-10 px-4 rounded-full border border-[#d6d9ce] bg-white text-[#1c2723] hover:bg-[#f5f6f2] transition-colors text-sm font-medium"
              >
                <Timer size={14} />
                Test geçmişi
              </Link>
              <button
                type="button"
                onClick={handleResetStatistics}
                className="inline-flex items-center justify-center h-10 px-4 rounded-full border border-[#a55e3f]/30 bg-white text-[#a55e3f] hover:bg-[#a55e3f] hover:text-white transition-colors text-sm font-medium"
              >
                İstatistikleri sıfırla
              </button>
            </div>
          </div>
        </section>

        <section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-serif-fact text-xl text-[#1c2723] flex items-center gap-2">
                <AlertTriangle size={18} className="text-[#2e4733]" />
                Mantıksız Önermeler
              </h2>
              <p className="text-sm text-[#5c635e] mt-1">
                Öğrencilerin anlam veya mantık yönünden sorunlu bulduğu kartlar.
              </p>
            </div>
            <span className="label-eyebrow text-[#a3aba6]">
              {logicReports.length} bildirim
            </span>
          </div>
          {logicReports.length === 0 ? (
            <div className="rounded-xl bg-[#f5f6f2] border border-[#e8eae3] px-4 py-5 text-sm text-[#5c635e]">
              Henüz mantık bildirimi yok.
            </div>
          ) : (
            <ol className="space-y-2 max-h-72 overflow-y-auto thin-scroll pr-1">
              {logicReports.map((report) => (
                <li key={report.id} className="rounded-xl border border-[#e8eae3] px-4 py-3 bg-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="label-eyebrow text-[#1c2723]">{report.category_name || "Kategori"}</span>
                        <span className="label-eyebrow text-[#a3aba6]">{report.count || 1} kez</span>
                        {(report.reporter_usernames || report.reporter_username) && (
                          <span className="label-eyebrow text-[#2e4733]">
                            Bildiren: {report.reporter_usernames || report.reporter_username}
                          </span>
                        )}
                      </div>
                      <p className="font-serif-fact text-[15px] text-[#1c2723] leading-snug">{report.text}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleOpenReportCategory(report)}
                        className="h-8 px-3 rounded-full bg-[#f5f6f2] border border-[#e8eae3] text-xs text-[#5c635e] hover:bg-[#e8eae3]"
                      >
                        Kategoriyi aç
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteLogicReport(report)}
                        className="h-8 w-8 rounded-full hover:bg-[#4a6741]/10 flex items-center justify-center text-[#4a6741]"
                        title="İncelendi olarak kaldır"
                      >
                        <CheckCircle2 size={15} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-serif-fact text-xl text-[#1c2723] flex items-center gap-2">
                <Plus size={18} className="text-[#2e4733]" />
                Kullanıcı Önerileri
              </h2>
              <p className="text-sm text-[#5c635e] mt-1">
                Kullanıcıların kart havuzuna eklenmek üzere gönderdiği yeni önermeler.
              </p>
            </div>
            <span className="label-eyebrow text-[#a3aba6]">
              {suggestions.length} öneri
            </span>
          </div>
          {suggestions.length === 0 ? (
            <div className="rounded-xl bg-[#f5f6f2] border border-[#e8eae3] px-4 py-5 text-sm text-[#5c635e]">
              Henüz bekleyen öneri yok.
            </div>
          ) : (
            <ol className="space-y-2 max-h-72 overflow-y-auto thin-scroll pr-1">
              {suggestions.map((suggestion) => (
                <li key={suggestion.id} className="rounded-xl border border-[#e8eae3] px-4 py-3 bg-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="label-eyebrow text-[#1c2723]">{suggestion.category_name || "Kategori"}</span>
                        <span className={`inline-flex items-center px-2 h-5 rounded-full text-[10px] font-medium uppercase tracking-wider ${suggestion.is_true ? "bg-[#4a6741]/10 text-[#4a6741]" : "bg-[#a55e3f]/10 text-[#a55e3f]"}`}>
                          {suggestion.is_true ? "Doğru" : "Yanlış"}
                        </span>
                        {suggestion.username && (
                          <span className="label-eyebrow text-[#2e4733]">Gönderen: {suggestion.username}</span>
                        )}
                      </div>
                      <p className="font-serif-fact text-[15px] text-[#1c2723] leading-snug">{suggestion.text}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleApproveSuggestion(suggestion)}
                        className="h-8 px-3 rounded-full bg-[#2e4733] text-white text-xs hover:bg-[#1c2723]"
                      >
                        Kabul et
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRejectSuggestion(suggestion)}
                        className="h-8 px-3 rounded-full border border-[#a55e3f]/30 text-[#a55e3f] text-xs hover:bg-[#a55e3f] hover:text-white"
                      >
                        Reddet
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="font-serif-fact text-xl text-[#1c2723] flex items-center gap-2">
                <AlertTriangle size={18} className="text-[#a55e3f]" />
                Hata Bildirimleri
              </h2>
              <p className="text-sm text-[#5c635e] mt-1">
                Öğrencilerin doğru/yanlış işaretinden şüphelendiği kartlar.
              </p>
            </div>
            <span className="label-eyebrow text-[#a3aba6]">
              {reports.length} bildirim
            </span>
          </div>

          {reports.length === 0 ? (
            <div className="rounded-xl bg-[#f5f6f2] border border-[#e8eae3] px-4 py-5 text-sm text-[#5c635e]">
              Henüz hata bildirimi yok.
            </div>
          ) : (
            <ol className="space-y-2 max-h-72 overflow-y-auto thin-scroll pr-1">
              {reports.map((report) => (
                <li
                  key={report.id}
                  className="rounded-xl border border-[#e8eae3] px-4 py-3 bg-[#ffffff]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="label-eyebrow text-[#1c2723]">
                          {report.category_name || "Kategori"}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 h-5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
                            report.expected_is_true
                              ? "bg-[#4a6741]/10 text-[#4a6741]"
                              : "bg-[#a55e3f]/10 text-[#a55e3f]"
                          }`}
                        >
                          Sistemde: {report.expected_is_true ? "Doğru" : "Yanlış"}
                        </span>
                        <span className="label-eyebrow text-[#a3aba6]">
                          {report.count || 1} kez
                        </span>
                        {(report.reporter_usernames || report.reporter_username) && (
                          <span className="label-eyebrow text-[#2e4733]">
                            Bildiren: {report.reporter_usernames || report.reporter_username}
                          </span>
                        )}
                      </div>
                      <p className="font-serif-fact text-[15px] text-[#1c2723] leading-snug">
                        {report.text}
                      </p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleOpenReportCategory(report)}
                        className="h-8 px-3 rounded-full bg-[#f5f6f2] border border-[#e8eae3] text-xs text-[#5c635e] hover:bg-[#e8eae3]"
                      >
                        Kategoriyi aç
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteReport(report)}
                        className="h-8 w-8 rounded-full hover:bg-[#4a6741]/10 flex items-center justify-center text-[#4a6741]"
                        title="İncelendi olarak kaldır"
                      >
                        <CheckCircle2 size={15} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className="bg-white border border-[#e8eae3] rounded-2xl p-5 mb-6">
  <div className="flex items-center justify-between gap-3 mb-3">
    <div>
      <h2 className="font-serif-fact text-xl text-[#1c2723]">
        Toplu Veri Yükle
      </h2>
      <p className="text-sm text-[#5c635e] mt-1">
        JSON formatındaki kategori ve doğru/yanlış önermeleri tek seferde yükleyin.
        Mükerrer kayıtlar otomatik atlanır.
      </p>
    </div>
    <Button
      onClick={handleBulkImport}
      disabled={bulkLoading || !bulkText.trim()}
      className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
    >
      {bulkLoading ? "Yükleniyor..." : "JSON Yükle"}
    </Button>
  </div>

  <Textarea
    value={bulkText}
    onChange={(e) => setBulkText(e.target.value)}
    rows={7}
    placeholder={`[
  {
    "kategori": "Medeni Hukuk",
    "text": "Hak ehliyeti haklara sahip olabilme yeteneğidir.",
    "is_true": true
  }
]`}
    className="font-mono text-xs"
  />
</section>
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Categories panel */}
          <section className="lg:col-span-4 bg-white border border-[#e8eae3] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif-fact text-xl text-[#1c2723]">Kategoriler</h2>
              <Button
                data-testid="add-category-btn"
                size="sm"
                onClick={() => setCatDialog({ open: true, initial: null })}
                className="rounded-full h-8 bg-[#2e4733] hover:bg-[#1c2723]"
              >
                <Plus size={14} className="mr-1" /> Yeni
              </Button>
            </div>

            <ul className="space-y-4" data-testid="admin-categories-list">
              {groupedCategories.map((group) => (
                <li key={group.id}>
                  <div className="label-eyebrow text-[#2e4733] mb-2 flex items-center justify-between">
                    <span>{group.name}</span>
                    <span className="text-[#a3aba6]">{group.categories.length}</span>
                  </div>
                  <ul className="space-y-2">
                    {group.categories.map((cat) => {
                      const active = selectedCategoryId === cat.id;
                      return (
                        <li key={cat.id}>
                          <div
                            className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 transition-colors ${
                              active
                                ? "border-[#4a6741]/40 bg-[#f5f6f2]"
                                : "border-transparent hover:bg-[#f5f6f2]"
                            } ${cat.hidden ? "opacity-60" : ""}`}
                          >
                            <button
                              data-testid={`admin-select-cat-${cat.id}`}
                              onClick={() => setSelectedCategoryId(cat.id)}
                              className="flex-1 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <FolderOpen
                                  size={14}
                                  className={active ? "text-[#4a6741]" : "text-[#a3aba6]"}
                                />
                                <span className="font-medium text-sm text-[#1c2723]">
                                  {cat.name}
                                </span>
                                {cat.hidden && (
                                  <span className="inline-flex items-center px-2 h-5 rounded-full bg-[#e8eae3] text-[10px] uppercase tracking-wider text-[#5c635e]">
                                    Gizli
                                  </span>
                                )}
                              </div>
                              <div className="label-eyebrow text-[#a3aba6] mt-1 ml-6">
                                {cat.fact_count} bilgi
                              </div>
                            </button>
                            <div className="flex items-center gap-1">
                              <select
                                value={getCategoryGroup(cat)}
                                onChange={(event) => handleCategoryGroupChange(cat, event.target.value)}
                                className="h-8 max-w-24 rounded-lg border border-[#d6d9ce] bg-white px-2 text-[11px] text-[#5c635e]"
                                title="Ders grubunu değiştir"
                                aria-label={`${cat.name} ders grubunu değiştir`}
                              >
                                {CATEGORY_GROUPS.map((option) => (
                                  <option key={option.id} value={option.id}>
                                    {option.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                data-testid={`toggle-cat-visibility-${cat.id}`}
                                onClick={() => handleToggleCategoryVisibility(cat)}
                                className="h-8 w-8 rounded-full hover:bg-[#e8eae3] flex items-center justify-center text-[#5c635e]"
                                title={cat.hidden ? "Ana sayfada göster" : "Ana sayfadan gizle"}
                              >
                                {cat.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                              </button>
                              <button
                                data-testid={`edit-cat-${cat.id}`}
                                onClick={() => setCatDialog({ open: true, initial: cat })}
                                className="h-8 w-8 rounded-full hover:bg-[#e8eae3] flex items-center justify-center text-[#5c635e]"
                              >
                                <Pencil size={13} />
                              </button>
                              <button
                                data-testid={`delete-cat-${cat.id}`}
                                onClick={() => handleDeleteCategory(cat)}
                                className="h-8 w-8 rounded-full hover:bg-red-50 flex items-center justify-center text-red-700"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                    {group.categories.length === 0 && (
                      <li className="rounded-xl border border-dashed border-[#e8eae3] px-3 py-3 text-xs text-[#a3aba6]">
                        Bu grupta kategori yok.
                      </li>
                    )}
                  </ul>
                </li>
              ))}              {categories.length === 0 && (
                <li className="text-sm text-[#5c635e] text-center py-8">
                  Henüz kategori yok.
                </li>
              )}
            </ul>
          </section>

          {/* Facts panel */}
          <section className="lg:col-span-8 bg-white border border-[#e8eae3] rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <div>
                <h2 className="font-serif-fact text-xl text-[#1c2723]">
                  Bilgiler
                </h2>
                {selectedCategory && (
                  <p className="label-eyebrow text-[#a3aba6] mt-1">
                    {selectedCategory.name}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={selectedCategoryId || undefined}
                  onValueChange={(v) => setSelectedCategoryId(v)}
                >
                  <SelectTrigger
                    data-testid="admin-cat-select"
                    className="w-56 rounded-full"
                  >
                    <SelectValue placeholder="Kategori seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {groupedCategories.map((group) => (
                      <React.Fragment key={group.id}>
                        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-[#a3aba6]">
                          {group.name}
                        </div>
                        {group.categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  data-testid="add-fact-btn"
                  disabled={!selectedCategoryId}
                  onClick={() => setFactDialog({ open: true, initial: null })}
                  className="rounded-full bg-[#2e4733] hover:bg-[#1c2723]"
                  size="sm"
                >
                  <Plus size={14} className="mr-1" /> Bilgi ekle
                </Button>
              </div>
            </div>

            {loadingFacts ? (
              <div className="py-12 text-center text-[#5c635e]">Yükleniyor…</div>
            ) : facts.length === 0 ? (
              <div className="py-12 text-center text-[#5c635e]" data-testid="empty-facts">
                Bu kategoride bilgi yok. Yukarıdan ekleyebilirsiniz.
              </div>
            ) : (
              <ol
                className="space-y-2 max-h-[60vh] overflow-y-auto thin-scroll pr-1"
                data-testid="admin-facts-list"
              >
                {facts.map((f, idx) => (
                  <li
                    key={f.id}
                    className="group flex items-start gap-3 rounded-xl border border-[#e8eae3] px-4 py-3 hover:bg-[#f5f6f2] transition-colors"
                  >
                    <span className="font-mono-key text-xs text-[#a3aba6] pt-0.5 w-8">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <span
                      className={`shrink-0 mt-0.5 inline-flex items-center px-2 h-5 rounded-full text-[10px] font-medium uppercase tracking-wider ${
                        f.is_true === false
                          ? "bg-[#a55e3f]/10 text-[#a55e3f]"
                          : "bg-[#4a6741]/10 text-[#4a6741]"
                      }`}
                    >
                      {f.is_true === false ? "Yanlış" : "Doğru"}
                    </span>
                    <p className="flex-1 font-serif-fact text-[15px] text-[#1c2723] leading-snug">
                      {f.text}
                    </p>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        data-testid={`edit-fact-${f.id}`}
                        onClick={() => setFactDialog({ open: true, initial: f })}
                        className="h-8 w-8 rounded-full hover:bg-white flex items-center justify-center text-[#5c635e]"
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        data-testid={`delete-fact-${f.id}`}
                        onClick={() => handleDeleteFact(f)}
                        className="h-8 w-8 rounded-full hover:bg-red-50 flex items-center justify-center text-red-700"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </main>

      <CategoryDialog
        key={`cat-${catDialog.initial?.id || "new"}-${catDialog.open}`}
        open={catDialog.open}
        onOpenChange={(o) => setCatDialog({ ...catDialog, open: o })}
        initial={catDialog.initial}
        onSaved={() => loadCategories()}
      />
      <FactDialog
        key={`fact-${factDialog.initial?.id || "new"}-${factDialog.open}`}
        open={factDialog.open}
        onOpenChange={(o) => setFactDialog({ ...factDialog, open: o })}
        initial={factDialog.initial}
        categoryId={selectedCategoryId}
        onSaved={() => {
          loadFacts(selectedCategoryId);
          loadCategories();
        }}
      />
      <Toaster />
    </div>
  );
}

